/**
 * Find My Real Needs — session flow.
 * Linear state machine matching backend `session.step`:
 *   my_position → my_needs → their_action → their_needs → synthesis
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  ArrowsClockwise,
  CheckCircle,
  Compass,
  DownloadSimple,
  FloppyDisk,
  Plus,
  Spinner,
  Sparkle,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Disclaimer from "../components/Disclaimer";
import FeedbackPrompt from "../components/FeedbackPrompt";
import { apiError } from "../lib/errors";

const STEP_ORDER = ["my_position", "my_needs", "their_action", "their_needs", "synthesis"];
const STEP_LABELS = {
  my_position: "Your position",
  my_needs: "Your needs",
  their_action: "The other person",
  their_needs: "Their imagined needs",
  synthesis: "Synthesis",
};

export default function NeedsSession() {
  const { id } = useParams();
  const nav = useNavigate();
  const { leader } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState(null);
  const [vocabulary, setVocabulary] = useState(null);
  const activeStepRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get(`/needs/${id}`)
      .then(({ data }) => { if (alive) setSession(data); })
      .catch((e) => { if (alive) toast.error(apiError(e, "Could not load session")); })
      .finally(() => { if (alive) setLoading(false); });
    api.get("/needs/vocabulary")
      .then(({ data }) => { if (alive) setVocabulary(data.categories); })
      .catch(() => {});
    return () => { alive = false; };
  }, [id]);

  // Scroll the active step into view whenever the step changes — otherwise the
  // new card can render off-screen below the confirmed-needs summary and users
  // think nothing happened.
  useEffect(() => {
    if (!activeStepRef.current || !session?.step) return;
    // Small delay so the new node has mounted before we scroll to it.
    const t = setTimeout(() => {
      activeStepRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(t);
  }, [session?.step]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center text-[#8A948E]">
        <Spinner size={24} className="inline-block animate-spin" /> Loading…
      </div>
    );
  }
  if (!session) return null;
  if (!leader) {
    nav("/auth/login?next=/needs");
    return null;
  }

  // Label the LLM-heavy transitions so the user knows the wait is expected.
  // Backend LLM calls can take 30–60s; without this the button just spins.
  const advanceLabelFor = (currentStep) => {
    if (currentStep === "my_position") return "Finding your needs…";
    if (currentStep === "their_action") return "Imagining their needs…";
    if (currentStep === "their_needs") return "Building the synthesis…";
    return null; // my_needs → their_action is instant (no LLM call)
  };

  const advance = async (payload) => {
    const label = advanceLabelFor(session.step);
    setBusy(true);
    setBusyLabel(label);
    try {
      const { data } = await api.post(`/needs/${id}/advance`, payload, { timeout: 120000 });
      setSession(data);
    } catch (e) {
      toast.error(apiError(e, "Could not advance"));
    } finally {
      setBusy(false);
      setBusyLabel(null);
    }
  };

  const digDeeper = async (side, alreadySelected) => {
    setBusy(true);
    setBusyLabel("Digging deeper…");
    try {
      const { data } = await api.post(
        `/needs/${id}/dig`,
        { side, already_selected: alreadySelected },
        { timeout: 120000 },
      );
      setSession(data);
    } catch (e) {
      toast.error(apiError(e, "Could not dig deeper"));
    } finally {
      setBusy(false);
      setBusyLabel(null);
    }
  };

  const resynthesize = async () => {
    setBusy(true);
    setBusyLabel("Rebuilding the synthesis…");
    try {
      const { data } = await api.post(`/needs/${id}/synthesize`, {}, { timeout: 120000 });
      setSession(data);
    } catch (e) {
      toast.error(apiError(e, "Could not regenerate synthesis"));
    } finally {
      setBusy(false);
      setBusyLabel(null);
    }
  };

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/needs/${id}/report.pdf`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `find-my-real-needs-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      toast.error(apiError(e, "Could not download PDF"));
    }
  };

  const step = session.step;
  const stepIndex = STEP_ORDER.indexOf(step);

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 py-10 lg:py-16">
      <button
        onClick={() => nav("/")}
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#8A948E] hover:text-[#1E3A2B]"
        data-testid="needs-back"
      >
        <ArrowLeft size={12} weight="bold" /> Home
      </button>

      <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 label-kicker" style={{ color: "#1E3A2B" }}>
          <Compass size={14} weight="duotone" /> Find My Real Needs
        </div>
        {session.step !== "synthesis" && (
          <button
            onClick={() => {
              toast.success("Progress saved. You can resume this session from Find My Real Needs.");
              nav("/needs");
            }}
            data-testid="needs-save-quit"
            className="inline-flex items-center gap-1.5 text-xs text-[#4A524C] hover:text-[#1E3A2B] px-3 py-1.5 rounded-lg border border-[#E8E4DB] hover:border-[#1E3A2B]"
            title="Your progress is saved automatically — you can come back to this later."
          >
            <FloppyDisk size={13} weight="duotone" /> Save & quit
          </button>
        )}
      </div>

      <Stepper step={step} />

      {/* What happened (always shown at top for context) */}
      <SectionCard title="What happened" data-testid="needs-situation-card">
        <p className="text-[#121815] leading-relaxed whitespace-pre-wrap">{session.situation}</p>
      </SectionCard>

      {stepIndex >= 0 && (
        <>
          {stepIndex >= 1 && (
            <SectionCard title="Your position" data-testid="needs-my-position-card">
              <p className="text-[#121815] leading-relaxed whitespace-pre-wrap">{session.my_position}</p>
            </SectionCard>
          )}

          {stepIndex >= 2 && session.my_confirmed?.length > 0 && (
            <SectionCard title="Your confirmed needs" data-testid="needs-my-confirmed-card">
              <NeedsDetailList values={session.my_confirmed} />
            </SectionCard>
          )}

          {stepIndex >= 3 && session.their_action && (
            <SectionCard title="What the other person said or did" data-testid="needs-their-action-card">
              <p className="text-[#121815] leading-relaxed whitespace-pre-wrap">{session.their_action}</p>
            </SectionCard>
          )}

          {stepIndex >= 4 && session.their_confirmed?.length > 0 && (
            <SectionCard title="Their imagined needs" data-testid="needs-their-confirmed-card">
              <NeedsDetailList values={session.their_confirmed} tone="peach" />
            </SectionCard>
          )}
        </>
      )}

      {/* ACTIVE STEP */}
      <div className="mt-8" ref={activeStepRef} data-testid="needs-active-step">
        {busyLabel && (
          <div
            className="mb-5 bg-[#F3F0EA] border border-[#1E3A2B]/20 rounded-2xl px-5 py-4 flex items-start gap-3 anim-in"
            data-testid="needs-thinking-banner"
          >
            <Spinner size={20} className="animate-spin text-[#1E3A2B] flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[#1E3A2B] font-medium">{busyLabel}</div>
              <p className="text-sm text-[#4A524C] mt-1 leading-relaxed">
                This can take a minute or two — the coach is drawing from the CNVC needs library and your situation.
              </p>
            </div>
          </div>
        )}
        {step === "my_position" && (
          <TextStep
            testid="needs-my-position-step"
            label="What is your position on what happened?"
            hint="What are you demanding, defending, or accusing? State it plainly — the exercise is to notice this is not yet your need."
            busy={busy}
            busyCaption="This may take a minute or two."
            onSubmit={(text) => advance({ text })}
            submitLabel="Find my needs"
          />
        )}

        {step === "my_needs" && (
          <NeedsPicker
            side="mine"
            testid="needs-my-needs-step"
            title="Your needs"
            intro="These are the deeper needs that might be driving your position. Your needs are what you really care about beneath the position — the problem you're trying to solve deep down. Pick the ones that resonate. You can also add your own or dig deeper for what sits underneath them."
            candidates={session.my_candidates || []}
            vocabulary={vocabulary}
            busy={busy}
            onDig={(already) => digDeeper("mine", already)}
            onConfirm={(needs) => advance({ confirmed: needs })}
            submitLabel="Now look at the other person"
          />
        )}

        {step === "their_action" && (
          <TextStep
            testid="needs-their-action-step"
            label="What did the other person say or do?"
            hint="Focus on their words and actions — not your interpretation of why."
            busy={busy}
            busyCaption="This may take a minute or two."
            onSubmit={(text) => advance({ text })}
            submitLabel="Imagine their needs"
          />
        )}

        {step === "their_needs" && (
          <NeedsPicker
            side="theirs"
            testid="needs-their-needs-step"
            title="What might they need?"
            intro="Imagine — without judging — what the other person might be trying to protect or achieve. What deeper needs could be driving their position? Pick or add ones that ring true."
            candidates={session.their_candidates || []}
            vocabulary={vocabulary}
            busy={busy}
            busyCaption="Building the synthesis may take a minute or two."
            onDig={(already) => digDeeper("theirs", already)}
            onConfirm={(needs) => advance({ confirmed: needs })}
            submitLabel="See the synthesis"
          />
        )}

        {step === "synthesis" && session.synthesis && (
          <SynthesisCard
            synth={session.synthesis}
            myConfirmed={session.my_confirmed || []}
            theirConfirmed={session.their_confirmed || []}
            busy={busy}
            onRegenerate={resynthesize}
            onDownload={downloadPdf}
          />
        )}
      </div>

      <Disclaimer variant="compact" className="mt-12 max-w-2xl mx-auto text-center" />
    </div>
  );
}

// =========================================================================
// Sub-components
// =========================================================================
function Stepper({ step }) {
  return (
    <div className="mt-2 mb-8 flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid="needs-stepper">
      {STEP_ORDER.map((s, i) => {
        const active = STEP_ORDER.indexOf(step) >= i;
        return (
          <span key={s} className={active ? "text-[#1E3A2B] font-medium" : "text-[#8A948E]"}>
            {i + 1}. {STEP_LABELS[s]}
          </span>
        );
      })}
    </div>
  );
}

function SectionCard({ title, children, ...props }) {
  return (
    <div className="mt-5 bg-white border border-[#E8E4DB] rounded-2xl px-5 py-4" {...props}>
      <div className="label-kicker mb-2" style={{ color: "#1E3A2B" }}>{title}</div>
      {children}
    </div>
  );
}

function NeedsBadges({ values }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="needs-badges">
      {values.map((v) => {
        const name = typeof v === "string" ? v : v.name;
        return (
          <span key={name} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F3F0EA] border border-[#E8E4DB] text-sm text-[#1E3A2B]">
            <CheckCircle size={12} weight="fill" /> {name}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Expanded view of confirmed needs — name + category + situation-specific
 * rationale for each. Used on the confirmed-needs summary cards and the
 * synthesis. Falls back to a plain badge chip for legacy string-only entries.
 */
function NeedsDetailList({ values, tone = "green" }) {
  if (!values?.length) return null;
  const border = tone === "green" ? "border-[#E8E4DB]" : "border-[#C77D5E]/25";
  const accent = tone === "green" ? "#1E3A2B" : "#C77D5E";
  return (
    <div className="space-y-2.5" data-testid="needs-detail-list">
      {values.map((v, i) => {
        // Backward-compat: legacy sessions may still return plain strings.
        const item = typeof v === "string" ? { name: v } : v || {};
        return (
          <div
            key={(item.name || "") + i}
            className={`bg-white border ${border} rounded-xl px-4 py-3`}
          >
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-medium text-[#1E3A2B]" style={{ color: accent }}>{item.name}</span>
              {item.category && (
                <span className="text-xs text-[#8A948E]">· {item.category}</span>
              )}
            </div>
            {item.rationale && (
              <p className="text-sm text-[#4A524C] mt-1 leading-relaxed">
                {item.rationale}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TextStep({ label, hint, busy, busyCaption, onSubmit, submitLabel, testid }) {
  const [text, setText] = useState("");
  return (
    <div className="bg-white border border-[#E8E4DB] rounded-2xl p-6" data-testid={testid}>
      <div className="label-kicker mb-2" style={{ color: "#1E3A2B" }}>{label}</div>
      <p className="text-sm text-[#4A524C] mb-4 leading-relaxed">{hint}</p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder="Type here…"
        data-testid={`${testid}-input`}
        className="w-full bg-white border border-[#E8E4DB] rounded-xl p-3 text-base focus:outline-none focus:border-[#1E3A2B]"
      />
      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        {busyCaption ? (
          <span className="text-xs text-[#8A948E] italic">{busyCaption}</span>
        ) : <span />}
        <button
          onClick={() => onSubmit(text)}
          disabled={busy || text.trim().length < 3}
          data-testid={`${testid}-submit`}
          className="inline-flex items-center gap-2 bg-[#1E3A2B] text-white px-5 py-2.5 rounded-xl hover:bg-[#2C4C3B] disabled:opacity-60"
        >
          {busy ? <Spinner size={14} className="animate-spin" /> : <ArrowRight size={14} weight="bold" />}
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

function NeedsPicker({ side, title, intro, candidates, vocabulary, busy, busyCaption, onDig, onConfirm, submitLabel, testid }) {
  const [selected, setSelected] = useState(new Set());
  const [customText, setCustomText] = useState("");
  const [showLibrary, setShowLibrary] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);

  const toggle = (name) => {
    const s = new Set(selected);
    if (s.has(name)) s.delete(name);
    else s.add(name);
    setSelected(s);
  };

  const addCustom = () => {
    const t = customText.trim();
    if (!t) return;
    const s = new Set(selected);
    s.add(t);
    setSelected(s);
    setCustomText("");
  };

  const confirm = () => {
    const list = Array.from(selected);
    if (!list.length) {
      toast.error("Pick at least one need to continue.");
      return;
    }
    onConfirm(list);
  };

  const dig = () => onDig(Array.from(selected));

  return (
    <div className="bg-white border border-[#E8E4DB] rounded-2xl p-6" data-testid={testid}>
      <div className="label-kicker mb-2" style={{ color: "#1E3A2B" }}>{title}</div>
      <p className="text-sm text-[#4A524C] leading-relaxed">{intro}</p>

      <div className="mt-5 space-y-2" data-testid={`${testid}-candidates`}>
        {candidates.map((c) => {
          const on = selected.has(c.name);
          return (
            <button
              key={c.name + (c.category || "")}
              onClick={() => toggle(c.name)}
              data-testid={`${testid}-candidate-${c.name}`}
              className={`w-full text-left px-4 py-3 rounded-xl border transition ${on ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "bg-white border-[#E8E4DB] hover:border-[#1E3A2B]"}`}
            >
              <div className="flex items-center gap-2">
                {on ? <CheckCircle size={16} weight="fill" /> : <Plus size={16} weight="bold" className="text-[#8A948E]" />}
                <span className="font-medium">{c.name}</span>
                {c.category && (
                  <span className={`text-xs ${on ? "text-white/70" : "text-[#8A948E]"}`}>· {c.category}</span>
                )}
              </div>
              {c.rationale && (
                <p className={`text-sm mt-1 ml-6 ${on ? "text-white/85" : "text-[#4A524C]"}`}>{c.rationale}</p>
              )}
            </button>
          );
        })}
      </div>

      {/* Custom need */}
      <div className="mt-5 pt-5 border-t border-[#E8E4DB]">
        <div className="flex gap-2">
          <input
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
            placeholder="Add a need in your own words…"
            data-testid={`${testid}-custom-input`}
            className="flex-1 bg-white border border-[#E8E4DB] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1E3A2B]"
          />
          <button
            onClick={addCustom}
            disabled={!customText.trim()}
            data-testid={`${testid}-custom-add`}
            className="inline-flex items-center gap-1 bg-[#F3F0EA] border border-[#E8E4DB] rounded-xl px-4 py-2 text-sm hover:border-[#1E3A2B] disabled:opacity-60"
          >
            <Plus size={14} weight="bold" /> Add
          </button>
        </div>

        <button
          onClick={() => setShowLibrary((v) => !v)}
          className="mt-3 text-xs text-[#4A524C] hover:text-[#1E3A2B] underline"
          data-testid={`${testid}-library-toggle`}
        >
          {showLibrary ? "Hide" : "Show"} the full CNVC needs library
        </button>
        {showLibrary && vocabulary && (
          <div className="mt-3 space-y-2 max-h-72 overflow-y-auto" data-testid={`${testid}-library`}>
            {Object.entries(vocabulary).map(([cat, items]) => {
              const open = openCategory === cat;
              return (
                <div key={cat} className="border border-[#E8E4DB] rounded-xl">
                  <button
                    onClick={() => setOpenCategory(open ? null : cat)}
                    className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-[#1E3A2B]"
                  >
                    <span>{cat}</span>
                    <span className="text-xs text-[#8A948E]">{items.length}</span>
                  </button>
                  {open && (
                    <div className="px-4 pb-3 flex flex-wrap gap-1.5">
                      {items.map((n) => {
                        const on = selected.has(n);
                        return (
                          <button
                            key={n}
                            onClick={() => toggle(n)}
                            className={`text-xs px-2.5 py-1 rounded-full border ${on ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "bg-white border-[#E8E4DB] text-[#4A524C] hover:border-[#1E3A2B]"}`}
                          >
                            {n}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected summary */}
      {selected.size > 0 && (
        <div className="mt-5 pt-5 border-t border-[#E8E4DB]">
          <div className="label-kicker mb-2" style={{ color: "#1E3A2B" }}>Selected ({selected.size})</div>
          <NeedsBadges values={Array.from(selected)} />
        </div>
      )}

      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={dig}
          disabled={busy || selected.size === 0}
          data-testid={`${testid}-dig`}
          className="inline-flex items-center gap-2 text-sm text-[#4A524C] hover:text-[#1E3A2B] disabled:opacity-50"
          title="Ask the coach what sits underneath the needs you've selected so far"
        >
          {busy ? <Spinner size={14} className="animate-spin" /> : <Sparkle size={14} weight="duotone" />}
          Dig deeper — what's underneath these?
        </button>
        <div className="flex items-center gap-3">
          {busyCaption && (
            <span className="text-xs text-[#8A948E] italic hidden sm:inline">{busyCaption}</span>
          )}
          <button
            onClick={confirm}
            disabled={busy || selected.size === 0}
            data-testid={`${testid}-confirm`}
            className="inline-flex items-center gap-2 bg-[#1E3A2B] text-white px-5 py-2.5 rounded-xl hover:bg-[#2C4C3B] disabled:opacity-60"
          >
            {busy ? <Spinner size={14} className="animate-spin" /> : null}
            {submitLabel} <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
      {busyCaption && (
        <p className="mt-2 text-xs text-[#8A948E] italic sm:hidden text-right">{busyCaption}</p>
      )}
    </div>
  );
}

function SynthesisCard({ synth, myConfirmed, theirConfirmed, busy, onRegenerate, onDownload }) {
  // Match names emitted by the LLM (plain strings) against the confirmed
  // NeedsCandidate objects on either side so we can carry over the rationale
  // to the synthesis view. Falls back to name-only if no match.
  const lookup = (name, side) => {
    const list = side === "mine" ? myConfirmed : theirConfirmed;
    const arr = Array.isArray(list) ? list : [];
    const lowered = String(name).toLowerCase();
    for (const item of arr) {
      const itemName = typeof item === "string" ? item : item?.name;
      if (itemName && itemName.toLowerCase() === lowered) {
        return typeof item === "string" ? { name: item } : item;
      }
    }
    return { name };
  };

  // Shared needs — try both sides for a rationale
  const shared = (synth.shared_needs || []).map((n) => {
    const fromMine = lookup(n, "mine");
    if (fromMine.rationale) return fromMine;
    return lookup(n, "theirs");
  });
  const yours = (synth.divergent_needs?.yours || []).map((n) => lookup(n, "mine"));
  const theirs = (synth.divergent_needs?.theirs || []).map((n) => lookup(n, "theirs"));

  return (
    <div className="bg-white border border-[#E8E4DB] rounded-2xl p-6 sm:p-8" data-testid="needs-synthesis-card">
      <div className="label-kicker mb-2" style={{ color: "#1E3A2B" }}>Synthesis</div>
      <h2 className="font-serif text-2xl sm:text-3xl text-[#1E3A2B] tracking-tight leading-tight">
        Here's what you've uncovered.
      </h2>

      <div className="mt-6 space-y-8">
        <div>
          <div className="label-kicker mb-3">Shared needs</div>
          {shared.length ? (
            <NeedsDetailList values={shared} />
          ) : (
            <p className="text-sm text-[#8A948E]">No overlap identified.</p>
          )}
        </div>

        <div>
          <div className="label-kicker mb-3">Where you diverge</div>
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-[0.12em] text-[#1E3A2B] mb-2">Yours</div>
              {yours.length ? (
                <NeedsDetailList values={yours} />
              ) : (
                <p className="text-sm text-[#8A948E]">—</p>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.12em] text-[#C77D5E] mb-2">Theirs</div>
              {theirs.length ? (
                <NeedsDetailList values={theirs} tone="peach" />
              ) : (
                <p className="text-sm text-[#8A948E]">—</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="label-kicker mb-2">Reframe</div>
          <p className="text-[#121815] leading-relaxed">{synth.reframe}</p>
        </div>

        <div>
          <div className="label-kicker mb-2">An opening sentence you could use</div>
          <p
            className="text-[#121815] leading-relaxed italic border-l-2 border-[#1E3A2B] pl-4"
            data-testid="needs-opening-sentence"
          >
            "{synth.opening_sentence}"
          </p>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-[#E8E4DB] flex flex-wrap gap-3 items-center justify-between">
        <button
          onClick={onRegenerate}
          disabled={busy}
          data-testid="needs-regenerate"
          className="inline-flex items-center gap-2 text-sm text-[#4A524C] hover:text-[#1E3A2B] disabled:opacity-60"
        >
          {busy ? <Spinner size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />}
          Regenerate
        </button>
        <button
          onClick={onDownload}
          data-testid="needs-download-pdf"
          className="inline-flex items-center gap-2 bg-[#1E3A2B] text-white px-5 py-2.5 rounded-xl hover:bg-[#2C4C3B]"
        >
          <DownloadSimple size={16} weight="bold" /> Download PDF
        </button>
      </div>
    </div>
  );
}
